import React, { useState, useEffect } from 'react';
import './FakturafordelingWizard.css';

const API_URL = 'https://advokat-api.swagner.workers.dev';

function FakturafordelingWizard({ token, onClose, onComplete }) {
  const [step, setStep] = useState('klient'); // 'klient', 'sak', 'faktura', 'resultat'
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [forsikringsselskaper, setForsikringsselskaper] = useState([]);

  // Klient state
  const [klientId, setKlientId] = useState(null);
  const [klientSearch, setKlientSearch] = useState('');
  const [klientData, setKlientData] = useState({
    navn: '',
    telefon: '',
    epost: '',
    adresse: '',
    postnr: '',
    poststed: '',
    type: 'person'
  });
  const [rettshjelp, setRettshjelp] = useState({
    bkf_selskap_id: '',
    bkf_timesats: 1375,
    bkf_tillegg: 0,
    bkf_polisenummer: '',
    innbo_selskap_id: '',
    innbo_dekningsgrad: 0.8,
    innbo_maks_belop: 100000,
    innbo_aktiveringsgebyr: 4000,
    innbo_polisenummer: ''
  });

  // Sak state
  const [sakData, setSakData] = useState({
    saksnummer: '',
    tittel: '',
    sakstype: 'boligkjøpersak',
    beskrivelse: ''
  });

  // Faktura state
  const [fakturaData, setFakturaData] = useState({
    klient_timesats: 2975,
    innbo_ikraftsettelsesdato: '',
    faktura_text: ''
  });

  // Resultat state
  const [resultat, setResultat] = useState(null);

  useEffect(() => {
    loadForsikringsselskaper();
  }, []);

  const loadForsikringsselskaper = async () => {
    try {
      const response = await fetch(`${API_URL}/forsikringsselskaper`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setForsikringsselskaper(data.selskaper || []);
    } catch (error) {
      console.error('Feil ved lasting av forsikringsselskaper:', error);
    }
  };

  const sokKlient = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/klienter/sok?navn=${encodeURIComponent(query)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setSearchResults(data.klienter || []);
    } catch (error) {
      console.error('Feil ved søk:', error);
    }
  };

  const velgKlient = async (klient) => {
    setKlientId(klient.id);
    setKlientData({ navn: klient.navn, telefon: klient.telefon, epost: klient.epost });
    setSearchResults([]);

    // Hent rettshjelpsinfo
    try {
      const response = await fetch(`${API_URL}/klienter/${klient.id}/rettshjelp`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.rettshjelp) {
        setRettshjelp({
          bkf_selskap_id: data.rettshjelp.bkf.selskap_id || '',
          bkf_timesats: data.rettshjelp.bkf.timesats || 1375,
          bkf_tillegg: data.rettshjelp.bkf.tillegg || 0,
          bkf_polisenummer: data.rettshjelp.bkf.polisenummer || '',
          innbo_selskap_id: data.rettshjelp.innbo.selskap_id || '',
          innbo_dekningsgrad: data.rettshjelp.innbo.dekningsgrad || 0.8,
          innbo_maks_belop: data.rettshjelp.innbo.maks_belop || 100000,
          innbo_aktiveringsgebyr: data.rettshjelp.innbo.aktiveringsgebyr || 4000,
          innbo_polisenummer: data.rettshjelp.innbo.polisenummer || ''
        });
      }
    } catch (error) {
      console.error('Kunne ikke hente rettshjelpsinfo:', error);
    }
  };

  const sendFakturafordeling = async () => {
    setLoading(true);

    const wizardData = {
      // Klient
      klient_id: klientId,
      klient_navn: klientId ? undefined : klientData.navn,
      klient_telefon: klientId ? undefined : klientData.telefon,
      klient_epost: klientId ? undefined : klientData.epost,
      klient_adresse: klientId ? undefined : klientData.adresse,
      klient_postnr: klientId ? undefined : klientData.postnr,
      klient_poststed: klientId ? undefined : klientData.poststed,
      klient_type: klientId ? undefined : klientData.type,

      // Rettshjelp (kun for ny klient)
      rettshjelp_bkf_selskap_id: klientId ? undefined : rettshjelp.bkf_selskap_id,
      rettshjelp_bkf_timesats: klientId ? undefined : rettshjelp.bkf_timesats,
      rettshjelp_bkf_tillegg: klientId ? undefined : rettshjelp.bkf_tillegg,
      rettshjelp_bkf_polisenummer: klientId ? undefined : rettshjelp.bkf_polisenummer,
      rettshjelp_innbo_selskap_id: klientId ? undefined : rettshjelp.innbo_selskap_id,
      rettshjelp_innbo_dekningsgrad: klientId ? undefined : rettshjelp.innbo_dekningsgrad,
      rettshjelp_innbo_maks_belop: klientId ? undefined : rettshjelp.innbo_maks_belop,
      rettshjelp_innbo_aktiveringsgebyr: klientId ? undefined : rettshjelp.innbo_aktiveringsgebyr,
      rettshjelp_innbo_polisenummer: klientId ? undefined : rettshjelp.innbo_polisenummer,

      // Sak
      saksnummer: sakData.saksnummer || undefined,
      sak_tittel: sakData.tittel || 'Fakturafordeling',
      sakstype: sakData.sakstype,
      saksbeskrivelse: sakData.beskrivelse || 'Automatisk opprettet ved fakturafordeling',

      // Faktura
      klient_timesats: fakturaData.klient_timesats,
      innbo_ikraftsettelsesdato: fakturaData.innbo_ikraftsettelsesdato ?
        new Date(fakturaData.innbo_ikraftsettelsesdato).getTime() : undefined,
      faktura_text: fakturaData.faktura_text
    };

    try {
      const response = await fetch(`${API_URL}/fakturafordeling/wizard`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(wizardData)
      });

      const data = await response.json();

      if (data.success) {
        setResultat(data);
        setStep('resultat');
      } else {
        alert('Feil: ' + (data.error || 'Ukjent feil'));
      }
    } catch (error) {
      alert('Feil ved fakturafordeling: ' + error.message);
    }

    setLoading(false);
  };

  const renderKlientStep = () => (
    <div className="wizard-step">
      <h2>🔍 Klient</h2>

      <div className="search-section">
        <label>Søk etter eksisterende klient:</label>
        <input
          type="text"
          placeholder="Navn, telefon eller e-post"
          value={klientSearch}
          onChange={(e) => {
            setKlientSearch(e.target.value);
            sokKlient(e.target.value);
          }}
          className="search-input"
        />

        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map(k => (
              <div key={k.id} className="search-result-item" onClick={() => velgKlient(k)}>
                <div className="result-name">{k.navn}</div>
                <div className="result-details">{k.telefon} • {k.epost}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {klientId ? (
        <div className="selected-klient">
          <div className="success-badge">✅ Eksisterende klient valgt</div>
          <div className="klient-info">
            <strong>{klientData.navn}</strong>
            <div>{klientData.telefon} • {klientData.epost}</div>
          </div>
          <button onClick={() => { setKlientId(null); setKlientData({ navn: '', telefon: '', epost: '' }); }} className="change-btn">
            Velg annen klient
          </button>
        </div>
      ) : (
        <div className="new-klient-form">
          <h3>Opprett ny klient</h3>
          <input type="text" placeholder="Navn *" value={klientData.navn} onChange={(e) => setKlientData({...klientData, navn: e.target.value})} required />
          <input type="tel" placeholder="Telefon" value={klientData.telefon} onChange={(e) => setKlientData({...klientData, telefon: e.target.value})} />
          <input type="email" placeholder="E-post" value={klientData.epost} onChange={(e) => setKlientData({...klientData, epost: e.target.value})} />
          <input type="text" placeholder="Adresse" value={klientData.adresse} onChange={(e) => setKlientData({...klientData, adresse: e.target.value})} />
          <div className="input-row">
            <input type="text" placeholder="Postnr" value={klientData.postnr} onChange={(e) => setKlientData({...klientData, postnr: e.target.value})} />
            <input type="text" placeholder="Poststed" value={klientData.poststed} onChange={(e) => setKlientData({...klientData, poststed: e.target.value})} />
          </div>

          <h4>Rettshjelp - Boligkjøperforsikring</h4>
          <select value={rettshjelp.bkf_selskap_id} onChange={(e) => setRettshjelp({...rettshjelp, bkf_selskap_id: e.target.value})}>
            <option value="">Velg selskap</option>
            {forsikringsselskaper.filter(f => f.type === 'boligkjoper').map(f => (
              <option key={f.id} value={f.id}>{f.navn}</option>
            ))}
          </select>
          <div className="input-row">
            <input type="number" placeholder="Timesats" value={rettshjelp.bkf_timesats} onChange={(e) => setRettshjelp({...rettshjelp, bkf_timesats: parseFloat(e.target.value)})} />
            <input type="number" placeholder="Tillegg per time" value={rettshjelp.bkf_tillegg} onChange={(e) => setRettshjelp({...rettshjelp, bkf_tillegg: parseFloat(e.target.value)})} />
          </div>
          <input type="text" placeholder="Polisenummer" value={rettshjelp.bkf_polisenummer} onChange={(e) => setRettshjelp({...rettshjelp, bkf_polisenummer: e.target.value})} />

          <h4>Rettshjelp - Innbo/Bygg</h4>
          <select value={rettshjelp.innbo_selskap_id} onChange={(e) => setRettshjelp({...rettshjelp, innbo_selskap_id: e.target.value})}>
            <option value="">Velg selskap</option>
            {forsikringsselskaper.filter(f => f.type === 'innbo').map(f => (
              <option key={f.id} value={f.id}>{f.navn}</option>
            ))}
          </select>
          <div className="input-row">
            <input type="number" step="0.01" placeholder="Dekningsgrad (0.8 = 80%)" value={rettshjelp.innbo_dekningsgrad} onChange={(e) => setRettshjelp({...rettshjelp, innbo_dekningsgrad: parseFloat(e.target.value)})} />
            <input type="number" placeholder="Maks beløp" value={rettshjelp.innbo_maks_belop} onChange={(e) => setRettshjelp({...rettshjelp, innbo_maks_belop: parseFloat(e.target.value)})} />
          </div>
        </div>
      )}

      <div className="wizard-buttons">
        <button onClick={onClose} className="cancel-btn">Avbryt</button>
        <button onClick={() => setStep('sak')} disabled={!klientId && !klientData.navn} className="next-btn">
          Neste →
        </button>
      </div>
    </div>
  );

  const renderSakStep = () => (
    <div className="wizard-step">
      <h2>📁 Sak</h2>
      <p className="step-description">Saksinformasjon (autogenereres hvis ikke utfylt)</p>

      <input type="text" placeholder="Saksnummer (autogenereres)" value={sakData.saksnummer} onChange={(e) => setSakData({...sakData, saksnummer: e.target.value})} />
      <input type="text" placeholder="Tittel (valgfritt)" value={sakData.tittel} onChange={(e) => setSakData({...sakData, tittel: e.target.value})} />
      <select value={sakData.sakstype} onChange={(e) => setSakData({...sakData, sakstype: e.target.value})}>
        <option value="boligkjøpersak">Boligkjøpersak</option>
        <option value="avhendingsloven">Avhendingsloven</option>
        <option value="bustadoppføringslova">Bustadoppføringslova</option>
      </select>
      <textarea placeholder="Beskrivelse (valgfritt)" value={sakData.beskrivelse} onChange={(e) => setSakData({...sakData, beskrivelse: e.target.value})} rows="3" />

      <div className="wizard-buttons">
        <button onClick={() => setStep('klient')} className="back-btn">← Tilbake</button>
        <button onClick={() => setStep('faktura')} className="next-btn">Neste →</button>
      </div>
    </div>
  );

  const renderFakturaStep = () => (
    <div className="wizard-step">
      <h2>📄 Faktura</h2>

      <label>Din timesats (kr) *</label>
      <input type="number" placeholder="2975" value={fakturaData.klient_timesats} onChange={(e) => setFakturaData({...fakturaData, klient_timesats: parseFloat(e.target.value)})} required />

      <label>Innbo ikraftsettelsesdato (valgfritt)</label>
      <input type="date" value={fakturaData.innbo_ikraftsettelsesdato} onChange={(e) => setFakturaData({...fakturaData, innbo_ikraftsettelsesdato: e.target.value})} />

      <label>Fakturatekst (AI parser automatisk) *</label>
      <textarea
        placeholder="Lim inn fakturatekst her... eller last opp PDF (kommer snart)"
        value={fakturaData.faktura_text}
        onChange={(e) => setFakturaData({...fakturaData, faktura_text: e.target.value})}
        rows="12"
        required
      />

      <div className="wizard-buttons">
        <button onClick={() => setStep('sak')} className="back-btn">← Tilbake</button>
        <button onClick={sendFakturafordeling} disabled={loading || !fakturaData.faktura_text} className="submit-btn">
          {loading ? 'Beregner...' : '🧮 Beregn fordeling'}
        </button>
      </div>
    </div>
  );

  const renderResultatStep = () => {
    if (!resultat) return null;

    const { fordeling } = resultat;

    return (
      <div className="wizard-step resultat-step">
        <h2>✅ Fakturafordeling fullført!</h2>

        <div className="created-resources">
          <h3>Opprettet:</h3>
          {resultat.created.klient && !resultat.created.klient.existing && (
            <div className="resource-item">✨ Ny klient: {resultat.created.klient.navn}</div>
          )}
          {resultat.created.sak && !resultat.created.sak.existing && (
            <div className="resource-item">✨ Ny sak: {resultat.created.sak.saksnummer}</div>
          )}
          <div className="resource-item">✨ Faktura: {resultat.created.faktura.parsedData.fakturanummer || 'Parsed'}</div>
        </div>

        <div className="fordeling-summary">
          <h3>Fordeling:</h3>
          {fordeling.fordeling.bkf.aktiv && fordeling.fordeling.bkf.belop > 0 && (
            <div className="fordeling-item">
              <span className="label">Boligkjøperforsikring:</span>
              <span className="amount">kr {fordeling.fordeling.bkf.totalInklMva.toFixed(2)}</span>
            </div>
          )}
          {fordeling.fordeling.innbo.aktiv && fordeling.fordeling.innbo.belop > 0 && (
            <div className="fordeling-item">
              <span className="label">Innboforsikring:</span>
              <span className="amount">kr {fordeling.fordeling.innbo.totalInklMva.toFixed(2)}</span>
            </div>
          )}
          <div className="fordeling-item">
            <span className="label">Klient:</span>
            <span className="amount">kr {fordeling.fordeling.klient.totalInklMva.toFixed(2)}</span>
          </div>
          <div className="fordeling-total">
            <span className="label">Totalt:</span>
            <span className="amount">
              kr {(
                (fordeling.fordeling.bkf.totalInklMva || 0) +
                (fordeling.fordeling.innbo.totalInklMva || 0) +
                fordeling.fordeling.klient.totalInklMva
              ).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="forklaring-section">
          <h3>Forklaring:</h3>
          <pre className="forklaring-text">{fordeling.forklaring}</pre>
        </div>

        <div className="wizard-buttons">
          <button onClick={() => { onComplete(); onClose(); }} className="done-btn">Ferdig</button>
        </div>
      </div>
    );
  };

  return (
    <div className="wizard-overlay">
      <div className="wizard-container">
        <div className="wizard-header">
          <h1>Fakturafordeling</h1>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="wizard-progress">
          <div className={`progress-step ${step === 'klient' ? 'active' : step !== 'klient' && step !== 'resultat' ? 'completed' : ''}`}>1. Klient</div>
          <div className={`progress-step ${step === 'sak' ? 'active' : step === 'faktura' || step === 'resultat' ? 'completed' : ''}`}>2. Sak</div>
          <div className={`progress-step ${step === 'faktura' ? 'active' : step === 'resultat' ? 'completed' : ''}`}>3. Faktura</div>
          <div className={`progress-step ${step === 'resultat' ? 'active' : ''}`}>4. Resultat</div>
        </div>

        <div className="wizard-content">
          {step === 'klient' && renderKlientStep()}
          {step === 'sak' && renderSakStep()}
          {step === 'faktura' && renderFakturaStep()}
          {step === 'resultat' && renderResultatStep()}
        </div>
      </div>
    </div>
  );
}

export default FakturafordelingWizard;
